"use client";

import { useQuery } from "@tanstack/react-query";
import { NOTIFICATIONS_REFETCH_INTERVAL_MS } from "@/features/notifications/constants";
import { useAccessToken } from "@/hooks/shared/useAccessToken";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { NotificationsApi } from "@/openapi/generated";
import { useNotificationsRefetchInterval } from "./useNotificationsRefetchInterval";

interface UseNotificationsUnreadCountGetProps {
  userId: number;
  refetchInterval?: number | false;
}

export const useNotificationsUnreadCountGet = ({
  userId,
  refetchInterval = NOTIFICATIONS_REFETCH_INTERVAL_MS,
}: UseNotificationsUnreadCountGetProps) => {
  const accessToken = useAccessToken();
  const effectiveRefetchInterval =
    useNotificationsRefetchInterval(refetchInterval);

  const { data, isError, isPending, isFetching, refetch } = useQuery({
    queryKey: [QueryKeys.NotificationsUnreadCount, userId],
    queryFn: () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(NotificationsApi, accessToken);
      return service.apiNotificationsUserIdUnreadCountGet({ userId });
    },
    enabled: !!accessToken && userId > 0,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    staleTime: 30 * 1000,
    refetchInterval: effectiveRefetchInterval,
  });

  return {
    data,
    count: Math.max(0, Number(data?.count ?? 0)),
    isError,
    isPending,
    isFetching,
    refetch,
  };
};
